import { FC, useEffect } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { getErrorFromCatch } from "../../api/api";
import { IPageParams } from "../../api/IApi";
import { orderApi } from "../../api/order.api";
import { IOrderFilterState } from "../../api/orderFilter";
import { useTypedDispatch } from "../../hooks/useTypedDispatch";
import { useTypedSelector } from "../../hooks/useTypedSelector";
import { IOrder } from "../../model/IOrder";
import { msg } from "../../msg";
import {
  getOrderListFilter,
  getOrderListPageParams,
  setOrderListFilter,
  setOrderListPageParams,
} from "../../store/orderSlice";
import priceFormatter from "../../utils/priceFormatter";
import {
  feedPrice,
  firstItem,
  freightDiscount,
  interchange,
  invoices,
  margin,
  partName,
  partPrice,
  salesOrderNumbers,
  subtotal,
  supplierName,
  vendorPrice,
} from "../../utils/orderTotals";
import { sumOf } from "../../utils/tableTotals";
import Truncate from "../common/Truncate";
import Table, { ITableTh } from "../common/table/Table";
import OrdersFilter from "./OrdersFilter";

// --
// -- Every column of the Recent Orders SQL view, in its order, under its own
// -- alias - these are the names people already read reports by, so they are
// -- not reworded. That includes `TotalChargback`, misspelt in the original.
// --
// -- sortCol is set on columns backed by a real Order column, which the server
// -- can sort on directly. The derived ones (sums over order_items, and the
// -- figures built from them) carry none - asking the server to sort by those
// -- would just 500. Supplier is the exception: it sorts on the nested path
// -- orderItems.supplierExternalKey, so by vendor key rather than vendor name.
// --
const thead: ITableTh[] = [
  { title: 'id', sortCol: 'id' },
  { title: 'OrderID', sortCol: 'orderId' },
  { title: 'Supplier', sortCol: 'orderItems.supplierExternalKey' },
  { title: 'PartName' },
  { title: 'Qty', sortCol: 'totalItems', align: 'center' },
  { title: 'Status', sortCol: 'status' },
  { title: 'Site', sortCol: 'fromSite' },
  { title: 'CID', sortCol: 'campaignId' },
  { title: 'Date', sortCol: 'timeCreate' },
  { title: 'Time' },
  { title: 'Updated', sortCol: 'timeUpdate' },
  { title: 'PartPrice', align: 'right' },
  { title: 'Coupon', sortCol: 'discountAmount', align: 'right' },
  { title: 'Fr8_Discount', align: 'right' },
  { title: 'Subtotal', sortCol: 'subtotalPrice', align: 'right' },
  { title: 'Shipping', sortCol: 'shipping', align: 'right' },
  { title: 'ShippingProtectionFee', sortCol: 'shippingProtectionFee', align: 'right' },
  { title: 'Handling', sortCol: 'handling', align: 'right' },
  { title: 'Taxes', sortCol: 'taxes', align: 'right' },
  { title: 'Core', sortCol: 'corePrice', align: 'right' },
  { title: 'Total', sortCol: 'totalPrice', align: 'right' },
  { title: 'vPrice', align: 'right' },
  { title: 'Margin', align: 'right' },
  { title: 'Ship_to_Bill_Dist', sortCol: 'billingShippingDistance', align: 'right' },
  { title: 'fPrice', align: 'right' },
  { title: 'Notes' },
  { title: 'Type', sortCol: 'siType' },
  { title: 'Name', sortCol: 'siName' },
  { title: 'BillingName', sortCol: 'piName' },
  { title: 'BillingStreet', sortCol: 'piStreet' },
  { title: 'BillingCity', sortCol: 'piCity' },
  { title: 'BillingState', sortCol: 'piState' },
  { title: 'BillingZip', sortCol: 'piZip' },
  { title: 'Company', sortCol: 'siCompany' },
  { title: 'Phone', sortCol: 'siPhone' },
  { title: 'Fax', sortCol: 'siFax' },
  { title: 'E-mail', sortCol: 'siEmail' },
  { title: 'ShipAddress', sortCol: 'siStreet' },
  { title: 'ShipCity', sortCol: 'siCity' },
  { title: 'ShipState', sortCol: 'siState' },
  { title: 'Zip', sortCol: 'siZip' },
  { title: 'ShipCountry', sortCol: 'siCountry' },
  { title: 'ShippingDate', sortCol: 'siDate' },
  { title: 'Carrier', sortCol: 'siCarrier' },
  { title: 'TrackingNum', sortCol: 'siTrackingNum' },
  { title: 'Referral', sortCol: 'entryFrom' },
  { title: 'Invoices' },
  { title: 'SalesOrderNumber' },
  { title: 'Nearby', sortCol: 'nearby' },
  { title: 'Distance', sortCol: 'distance', align: 'right' },
  { title: 'CouponCode', sortCol: 'couponCode' },
  { title: 'UserAgent' },
  { title: 'PaymentType', sortCol: 'piType' },
  { title: 'TransactionID' },
  { title: 'Make' },
  { title: 'Model' },
  { title: 'Year' },
  { title: 'mileage', align: 'right' },
  { title: 'Lender', sortCol: 'lender' },
  { title: 'IsTaxExempt', sortCol: 'isTaxExempt', align: 'center' },
  { title: 'IPAddress', sortCol: 'ip' },
  { title: 'Interchange' },
  { title: 'TotalChargback', align: 'right' },
  { title: 'TotalRefund', align: 'right' },
  { title: 'TotalVendorRefund', align: 'right' },
  { title: 'PartsCareStatus' },
  { title: 'PartsCareCoverage' },
  { title: 'PartsCareSubscriptionPrice', align: 'right' },
  { title: 'PartsCareRatePercent', align: 'right' },
];

/** Blank rather than $0.00 when the figure genuinely has no value. */
const money = (value: number | null | undefined) =>
  value === null || value === undefined ? '' : priceFormatter(value);

const num = (value: number | null | undefined) =>
  value === null || value === undefined ? '' : String(value);

const parsed = (value: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// -- DATE_FORMAT(time_create, '%Y-%m-%d')
const dateCell = (value: string | null) => {
  const date = parsed(value);
  return date ? date.toLocaleDateString('en-CA') : (value || '');
};

// -- DATE_FORMAT(time_create, '%T')
const timeCell = (value: string | null) => {
  const date = parsed(value);
  return date ? date.toLocaleTimeString('en-GB') : '';
};

const dateTimeCell = (value: string | null) => {
  const date = parsed(value);
  return date ? `${date.toLocaleDateString('en-CA')} ${date.toLocaleTimeString('en-GB')}` : (value || '');
};

const yesNo = (value: boolean | null) =>
  value === null || value === undefined ? '' : (value ? 'Yes' : 'No');

const text = (value: string | null | undefined) => (
  <Truncate title={value || ''}>{value || ''}</Truncate>
);

const nowrap = (value: string | null | undefined) => (
  <span className="whitespace-nowrap">{value || ''}</span>
);

// --
// -- The footer's total for a column, keyed by the header it sits under.
// --
// -- By title rather than by position: the body row is built as a 69-entry
// -- array and a footer built the same way would only have to be one entry out
// -- to put a money total under a phone number. Looking the column up by its
// -- own header means the two cannot drift apart.
// --
// -- Only additive columns appear here. Distance, Ship_to_Bill_Dist, mileage,
// -- Year, CID and PartsCareRatePercent are numbers but not quantities that add
// -- up to anything, so they stay blank rather than showing a meaningless sum.
// --
const TOTAL_BY_COLUMN: Record<string, (orders: IOrder[]) => React.ReactNode> = {
  'Qty': (orders) => sumOf(orders, (order) => order.totalItems) ?? '',
  'PartPrice': (orders) => money(sumOf(orders, partPrice)),
  'Coupon': (orders) => money(sumOf(orders, (order) => order.discountAmount)),
  'Fr8_Discount': (orders) => money(sumOf(orders, freightDiscount)),
  'Subtotal': (orders) => money(sumOf(orders, subtotal)),
  'Shipping': (orders) => money(sumOf(orders, (order) => order.shipping)),
  'ShippingProtectionFee': (orders) => money(sumOf(orders, (order) => order.shippingProtectionFee)),
  'Handling': (orders) => money(sumOf(orders, (order) => order.handling)),
  'Taxes': (orders) => money(sumOf(orders, (order) => order.taxes)),
  'Core': (orders) => money(sumOf(orders, (order) => order.corePrice)),
  'Total': (orders) => (
    <span className="whitespace-nowrap">{money(sumOf(orders, (order) => order.totalPrice))}</span>
  ),
  'vPrice': (orders) => money(sumOf(orders, vendorPrice)),
  'Margin': (orders) => {
    const total = sumOf(orders, margin);
    return (
      <span className={(total ?? 0) < 0 ? 'text-pink-600 whitespace-nowrap' : 'whitespace-nowrap'}>
        {money(total)}
      </span>
    );
  },
  'fPrice': (orders) => money(sumOf(orders, feedPrice)),
  'TotalChargback': (orders) => money(sumOf(orders, (order) => order.chargeback)),
  'TotalRefund': (orders) => money(sumOf(orders, (order) => order.refunds)),
  'TotalVendorRefund': (orders) => money(sumOf(orders, (order) => order.vendorRefunds)),
  'PartsCareSubscriptionPrice': (orders) => money(sumOf(orders, (order) => order.partscare?.subscriptionPrice)),
};

const OrderList: FC = () => {
  const dispatch = useTypedDispatch();
  const pageParams = useTypedSelector(getOrderListPageParams());
  const filter = useTypedSelector(getOrderListFilter());

  const { data: orders, isFetching, isError, error } = orderApi.useGetOrderListQuery({
    ...pageParams,
    ...filter,
  });

  useEffect(() => {
    isError && error && toast.error(`${msg('load-orders-filed')} ${getErrorFromCatch(error)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  const row = (order: IOrder) => {
    const item = firstItem(order);
    const partscare = order.partscare;

    return [
      num(order.id),
      <Link
        to={`/orders/${order.orderId}`}
        className="whitespace-nowrap font-semibold text-primary hover:text-primary_hover underline underline-offset-2"
      >
        {order.orderId}
      </Link>,
      text(supplierName(order)),
      text(partName(order)),
      order.totalItems ?? '',
      text(order.status),
      text(order.fromSiteCleaned || order.fromSite),
      text(order.campaignId),
      nowrap(dateCell(order.timeCreate)),
      <span className="whitespace-nowrap opacity-70">{timeCell(order.timeCreate)}</span>,
      nowrap(dateTimeCell(order.timeUpdate)),
      money(partPrice(order)),
      money(order.discountAmount),
      money(freightDiscount(order)),
      money(subtotal(order)),
      money(order.shipping),
      money(order.shippingProtectionFee),
      money(order.handling),
      money(order.taxes),
      money(order.corePrice),
      <span className="font-semibold whitespace-nowrap">{money(order.totalPrice)}</span>,
      money(vendorPrice(order)),
      <span className={(margin(order) ?? 0) < 0 ? 'text-pink-600 whitespace-nowrap' : 'whitespace-nowrap'}>
        {money(margin(order))}
      </span>,
      num(order.billingShippingDistance),
      money(feedPrice(order)),
      text(order.notes),
      text(order.siType),
      text(order.siName),
      text(order.piName),
      text(order.piStreet),
      text(order.piCity),
      text(order.piState),
      text(order.piZip),
      text(order.siCompany),
      nowrap(order.siPhone),
      nowrap(order.siFax),
      text(order.siEmail),
      text(order.siStreet),
      text(order.siCity),
      text(order.siState),
      text(order.siZip),
      text(order.siCountry),
      nowrap(dateCell(order.siDate)),
      text(order.siCarrier),
      text(order.siTrackingNum),
      text(order.entryFrom),
      text(invoices(order)),
      text(salesOrderNumbers(order)),
      text(order.nearby),
      num(order.distance),
      text(order.couponCode),
      text(order.userAgent),
      text(order.paymentType),
      text(order.transactionId),
      text(item?.viMake),
      text(item?.viModel),
      nowrap(item?.viYear),
      num(item?.mileage),
      text(order.lender),
      yesNo(order.isTaxExempt),
      nowrap(order.ip),
      text(interchange(order)),
      money(order.chargeback),
      money(order.refunds),
      money(order.vendorRefunds),
      text(partscare?.status),
      text(partscare?.coverage),
      money(partscare?.subscriptionPrice),
      partscare?.ratePercent === null || partscare?.ratePercent === undefined
        ? ''
        : `${partscare.ratePercent}%`,
    ];
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/*
        The filter sits outside the scrolling area on purpose. Inside it, the
        card becomes a scroll container in both axes, which clips the open
        Period list whenever the table is short - and the filter would slide
        out of view as soon as anyone scrolled the table sideways.
      */}
      <div className="flex justify-between gap-6 items-start px-4 sm:px-6 lg:px-8 pt-5">
        <OrdersFilter
          filter={filter}
          disabled={isFetching}
          onChange={(newFilter: IOrderFilterState) => dispatch(setOrderListFilter(newFilter))}
        />
        <div className="text-text_pale whitespace-nowrap pt-7">
          {orders?.totalElements ?? 0} order(s)
        </div>
      </div>

      <div className="overflow-x-auto stylledScroll pb-2">
      <Table
        size="small"
        isResizable
        resizeStorageKey="recent-orders"
        isPending={isFetching}
        pageParams={pageParams}
        pagable={orders}
        updatePageParams={(newParams: Partial<IPageParams>) =>
          dispatch(setOrderListPageParams({ ...pageParams, ...newParams }))
        }
        thead={thead}
        tbody={!orders?.content?.length
          ? undefined
          : orders.content.map((order) => ({
            className: order.fraudFlag ? 'bg-pink-50' : '',
            data: row(order),
          }))
        }
        // -- One page's worth. Labelled as such: the list is paginated, so a
        // -- plain "Total" here would read as the total for the whole filter.
        tfoot={!orders?.content?.length
          ? undefined
          : thead.map(({ title }, i) => {
            if (!i) return `Total (page ${(pageParams.page ?? 0) + 1})`;
            const total = title ? TOTAL_BY_COLUMN[title] : undefined;
            return total ? total(orders.content) : '';
          })
        }
      />
      </div>
    </div>
  );
};

export default OrderList;
